import React, { useEffect, useState, useRef } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Row,
    Form,
   
} from "reactstrap"
import { useFormik } from "formik"
import { validateBeforeConfirm } from "helpers/validation_helper"
import { useDispatch, useSelector } from "react-redux"
import {
    getMerchantPins,
    showMerchantPins,
    updateMerchantPins,
    clearMerchantPins,
} from "store/merchants/actions"
import * as Yup from "yup"
import ConfirmButton from "components/Common/ConfirmButton"
import TextInput from "components/Common/TextInput"
import PasswordInput from "components/Common/PasswordInput"
import CButton from "components/Common/CButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import IconButton from "components/Common/IconButton"
import * as url from "../../helpers/url_helper"

export default function MerchantPinsForm({
    match,
    onTabCompletion,
    activeTab,
    tabId,
    createdMerchantID,
    page,
}) {
    const [readOnly, setReadOnly] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const [encryptedPins, setEncryptedPins] = useState(null) // Encrypted PINs
    const [decryptedPins, setDecryptedPins] = useState(null) // Decrypted PINs (API returns both)
    const [showMerchantPin, setShowMerchantPin] = useState(false) // User clicked eye on merchant PIN
    const [showRefundPin, setShowRefundPin] = useState(false) // User clicked eye on refund PIN
    const previousError = useRef(null)

    const detailID = match?.params?.id || createdMerchantID

    const dispatch = useDispatch()
    const { 
        pinsError, 
        showPinsError,
        pinUpdateSuccess, 
        fetchedMerchantPins, 
        isLoadingPins,
        isShowingPins
    } = useSelector(state => ({
        pinsError: state.merchants.pinsError?.message || state.merchants.pinsError?.description,
        showPinsError: state.merchants.showPinsError?.message || state.merchants.showPinsError?.description,
        pinUpdateSuccess: state.merchants.pinUpdateSuccess,
        fetchedMerchantPins: state.merchants.merchantPins,
        isLoadingPins: state.merchants.isLoadingPins,
        isShowingPins: state.merchants.isShowingPins,
    }))

    useEffect(() => {
        // Only set initial state, don't override edit mode
        if (encryptedPins && readOnly === false) {
            // Don't change state if we're currently in edit mode
            return
        }

        if (encryptedPins) {
            setReadOnly(true)
        } else if (page === "detail") {
            // In detail mode with no data, start in readonly state (showing add button)
            setReadOnly(true)
            setShowAddForm(false)
        } else if (page === "add") {
            // In create mode, show form to add pins
            setReadOnly(false)
            setShowAddForm(true)
        }
    }, [encryptedPins, page, readOnly])

    // Fetch merchant pins only when tab is active
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (detailID && isTabActive) {
            const options = {
                headers: {
                    "input-parameters": { merchantID: detailID },
                },
            }
            dispatch(getMerchantPins(options))
        }

        if (!isTabActive) {
            // Reset decrypted state when leaving tab - so when returning, view shows encrypted first
            setDecryptedPins(null)
            setShowMerchantPin(false)
            setShowRefundPin(false)
        }

        return () => {
            dispatch(clearMerchantPins())
        }
    }, [dispatch, detailID, activeTab, tabId])

    // Set encrypted PINs from getMerchantPins
    useEffect(() => {
        if (fetchedMerchantPins && !isShowingPins) {
            setEncryptedPins(fetchedMerchantPins)
        }
    }, [fetchedMerchantPins, isShowingPins])

    // Set decrypted PINs from showMerchantPins (both PINs come together)
    useEffect(() => {
        if (isShowingPins && fetchedMerchantPins) {
            setDecryptedPins(fetchedMerchantPins)
        }
    }, [isShowingPins, fetchedMerchantPins])

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            merchantPin: encryptedPins?.redemptionPIN || "",
            refundPin: encryptedPins?.refundPIN || "",
        },
        validationSchema: Yup.object({
            merchantPin: Yup.string()
                .required("Please Enter Redemption PIN")
                .length(5, "Redemption PIN must be exactly 5 characters"),
            refundPin: Yup.string()
                .required("Please Enter Refund PIN")
                .length(5, "Refund PIN must be exactly 5 characters"),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleConfirmedSubmit()
            }
        },
    })

    // Swap merchant PIN value based on eye toggle - only in readOnly mode
    // In edit mode, user's typed value must be preserved
    useEffect(() => {
        if (!readOnly) return
        if (showMerchantPin) {
            validation.setFieldValue("merchantPin", encryptedPins?.redemptionPIN || "")
        } else if (decryptedPins?.redemptionPIN) {
            validation.setFieldValue("merchantPin", decryptedPins.redemptionPIN)
        }
    }, [showMerchantPin]) 
    // Swap refund PIN value based on eye toggle - only in readOnly mode
    useEffect(() => {
        if (!readOnly) return
        if (showRefundPin) {
            validation.setFieldValue("refundPin", encryptedPins?.refundPIN || "")
        } else if (decryptedPins?.refundPIN) {
            validation.setFieldValue("refundPin", decryptedPins.refundPIN)
        }
    }, [showRefundPin]) 
    // When decrypted PINs arrive from API, update fields that are currently hidden
    // Only in readOnly mode to avoid overwriting user edits
    useEffect(() => {
        if (!readOnly || !decryptedPins) return
        if (!showMerchantPin && decryptedPins.redemptionPIN) {
            validation.setFieldValue("merchantPin", decryptedPins.redemptionPIN)
        }
        if (!showRefundPin && decryptedPins.refundPIN) {
            validation.setFieldValue("refundPin", decryptedPins.refundPIN)
        }
    }, [decryptedPins]) 
    const handleConfirmedSubmit = async () => {
        setIsSubmitting(true)
        try {
            const values = validation.values
            const payload = {
                merchantID: detailID,
                redemptionPIN: values.merchantPin,
                refundPIN: values.refundPin,
            }

            dispatch(updateMerchantPins(payload, {}))
        } catch (error) {
            console.error("Form submission error:", error)
        }
    }

    const handleMerchantPinToggle = (show) => {
        setShowMerchantPin(show)
        // Call API only once if we don't have decrypted PINs yet
        if (show && !decryptedPins && detailID) {
            const options = {
                headers: {
                    "input-parameters": { merchantID: detailID },
                },
            }
            dispatch(showMerchantPins(options))
        }
    }

    const handleRefundPinToggle = (show) => {
        setShowRefundPin(show)
        // Call API only once if we don't have decrypted PINs yet
        if (show && !decryptedPins && detailID) {
            const options = {
                headers: {
                    "input-parameters": { merchantID: detailID },
                },
            }
            dispatch(showMerchantPins(options))
        }
    }

    useEffect(() => {
        if (pinUpdateSuccess && pinUpdateSuccess !== previousError.current) {
            setIsSubmitting(false)
            setReadOnly(true)
            setShowAddForm(false)
            setEncryptedPins(pinUpdateSuccess)
            // Reset visibility states
            setShowMerchantPin(false)
            setShowRefundPin(false)
            setDecryptedPins(null)
            previousError.current = pinUpdateSuccess

            if (onTabCompletion) {
                onTabCompletion(activeTab)
            }
        }
    }, [pinUpdateSuccess, onTabCompletion, activeTab])

    useEffect(() => {
        if ((pinsError || showPinsError) && (pinsError !== previousError.current || showPinsError !== previousError.current)) {
            setIsSubmitting(false)
            previousError.current = pinsError || showPinsError
        }
    }, [pinsError, showPinsError])

    const handleEdit = () => {
        setReadOnly(false)
        setShowAddForm(true)
        setShowMerchantPin(false)
        setShowRefundPin(false)
        setDecryptedPins(null)
    }

    const handleCancel = () => {
        setReadOnly(true)
        setShowAddForm(false)
        setShowMerchantPin(false)
        setShowRefundPin(false)
        setDecryptedPins(null)
        validation.resetForm()
        // Reload encrypted PINs
        if (detailID) {
            const options = {
                headers: {
                    "input-parameters": { merchantID: detailID },
                },
            }
            dispatch(getMerchantPins(options))
        }
    }

    if (isLoadingPins) {
        return (
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading PINs...</p>
                        </div>
                    </div>
                </CardBody>
            </Card>
        )
    }

    return (
        <>
            {(pinsError || showPinsError) && (
                <Alert color="danger" className="mb-3">
                    {pinsError || showPinsError}
                </Alert>
            )}

            {/* Add/Edit Form */}
            {(showAddForm || (encryptedPins && !readOnly)) && (
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                                {encryptedPins ? "Update PINs" : "Add PINs"}
                            </h5>
                        </div>

                        <Form
                            className="needs-validation"
                            onSubmit={(e) => {
                                e.preventDefault()
                                validation.handleSubmit()
                                return false
                            }}
                        >
                            <Row>
                                {/* Merchant PIN */}
                                <Col md="6">
                                    <PasswordInput
                                        label="Redemption PIN"
                                        name="merchantPin"
                                        placeholder="Enter 5-digit PIN"
                                        readOnly={readOnly}
                                        validation={validation}
                                        required={true}
                                        maxLength={5}
                                        helperText="Must be exactly 5 characters"
                                        showToggle={!readOnly}
                                        onToggleShow={handleMerchantPinToggle}
                                    />
                                </Col>

                                {/* Refund PIN */}
                                <Col md="6">
                                    <PasswordInput
                                        label="Refund PIN"
                                        name="refundPin"
                                        placeholder="Enter 5-digit Refund PIN"
                                        readOnly={readOnly}
                                        validation={validation}
                                        required={true}
                                        maxLength={5}
                                        helperText="Must be exactly 5 characters"
                                        showToggle={!readOnly}
                                        onToggleShow={handleRefundPinToggle}
                                    />
                                </Col>
                            </Row>

                            {!readOnly && (
                                <Row>
                                    <Col md="12" className="text-end">
                                        <CButton.Secondary
                                            text="Cancel"
                                            className="me-2"
                                            onClick={handleCancel}
                                            disabled={isSubmitting}
                                        />
                                        <ConfirmButton
                                            validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                            onConfirm={handleConfirmedSubmit}
                                            message={`Are you sure you want to ${encryptedPins ? "update" : "create"} these PINs?`}
                                            color="primary"
                                            disabled={isSubmitting}
                                            contextUri={url.UPDATE_MERCHANT_PINS}
                                            actionType="post"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                    {encryptedPins ? "Updating..." : "Creating..."}
                                                </>
                                            ) : (
                                                `${encryptedPins ? "Update" : "Create"} PINs`
                                            )}
                                        </ConfirmButton>
                                    </Col>
                                </Row>
                            )}
                        </Form>
                    </CardBody>
                </Card>
            )}

            {/* Display existing PINs */}
            {encryptedPins && readOnly && !showAddForm && (
                <Card>
                    <CardBody>
                        {/* Show edit button when data exists */}
                        <div className="d-flex justify-content-end mb-3">
                            <AccessControlWrapper
                                contextUri={url.UPDATE_MERCHANT_PINS}
                                actionType="post"
                            >
                                <CButton.Primary
                                    text="Edit PINs"
                                    onClick={handleEdit}
                                />
                            </AccessControlWrapper>
                        </div>

                        <Row>
                            <Col md="6">
                                <PasswordInput
                                    label="Redemption PIN"
                                    name="merchantPin"
                                    readOnly={true}
                                    validation={validation}
                                    required={false}
                                    emptyText="Redemption PIN not set"
                                    showToggle={true}
                                    onToggleShow={handleMerchantPinToggle}
                                />
                            </Col>
                            <Col md="6">
                                <PasswordInput
                                    label="Refund PIN"
                                    name="refundPin"
                                    readOnly={true}
                                    validation={validation}
                                    required={false}
                                    emptyText="Refund PIN not set"
                                    showToggle={true}
                                    onToggleShow={handleRefundPinToggle}
                                />
                            </Col>
                        </Row>

                    </CardBody>
                </Card>
            )}

            {/* No PINs found state */}
            {!encryptedPins && readOnly && !showAddForm && page === "detail" && (
                <Card>
                    <CardBody className="text-center py-5">
                        <div className="mb-4">
                            <i className="bx bx-lock-alt display-1 text-muted"></i>
                        </div>
                        <h5>No PINs Found</h5>
                        <p className="text-muted mb-4">
                            This merchant doesn't have PINs configured yet.
                        </p>
                        <AccessControlWrapper
                            contextUri={url.UPDATE_MERCHANT_PINS}
                            actionType="post"
                        >
                            <CButton.Primary
                                text="Add PINs"
                                icon="bx bx-plus"
                                onClick={() => {
                                    setReadOnly(false)
                                    setShowAddForm(true)
                                }}
                            />
                        </AccessControlWrapper>
                    </CardBody>
                </Card>
            )}
        </>
    )
}